import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import ComplianceShell from 'features/compliance/ComplianceShell'
import ComplianceScoreExplainer from 'features/compliance/ComplianceScoreExplainer'
import { RAG as RAG_LEVELS, scoreColour } from 'features/dashboard/charts/scoreColour'
import { COMPLIANCE_STATUS_BUCKETS, complianceBucketsFromRollup, complianceBucketTotal } from 'features/compliance/complianceStatusBuckets'
import ChartKeyExplainer from 'features/dashboard/charts/ChartKeyExplainer'
import styled, { keyframes } from 'styled-components'
import { CircularProgress } from '@mui/material'
import { SkeletonRows } from 'components/Skeleton'
import InfiniteScrollSentinel from 'components/InfiniteScrollSentinel'
import LaunchRoundedIcon from '@mui/icons-material/LaunchRounded'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import {
  getComplianceHistory,
  getComplianceDeepDive,
} from 'services/complianceService'
import { reviewObligationEvidence, bulkApproveEvidence } from 'services/checklistService'
import { fetchBpUnits } from 'services/unitsService'
import { apiErrorMessage } from 'utils/errors'
import { openInNewTab } from 'utils/openInNewTab'
import { notifyComplianceChanged, useRefetchOnFocus } from 'utils/complianceEvents'
import ActionsAPI from 'services/ActionsAPI'
import { toast } from 'react-toastify'
import ComplianceTrendChart from './ComplianceTrendChart'
import CustomiseObligationModal from './CustomiseObligationModal'
import GuruInsightModal from 'features/bpDetails/Components/files/GuruInsightModal'
import OnceCallout from 'features/compliance/OnceCallout'
import ObligationScopePill from 'features/compliance/ObligationScopePill'
import TuneRoundedIcon from '@mui/icons-material/TuneRounded'
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded'
import BuildRoundedIcon from '@mui/icons-material/BuildRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded'

const tintFor = scoreColour

const verdictTint = verdict => {
  switch (verdict) {
    case 'satisfied': return RAG_LEVELS.low.fg
    case 'expired':   return RAG_LEVELS.critical.fg
    case 'skipped':   return RAG_LEVELS.none.fg
    default:          return RAG_LEVELS.medium.fg
  }
}

const formatPct = v => v === null || v === undefined ? 'No score' : `${Math.round(v)}%`

const UNIT_PAGE = 12

const isUnitScoped = obl => (obl.scope || 'building') === 'unit'

const scopeSatisfyNote = obl => {
  const cov = obl.unitCoverage || obl.unit_coverage
  if (isUnitScoped(obl)) {
    return cov && cov.applicable
      ? `for each applicable unit. ${cov.covered} of ${cov.applicable} units are covered so far`
      : 'for each applicable unit'
  }
  return 'for the whole building'
}

const formatExpiry = (iso, daysUntilExpiry) => {
  if (!iso) return null
  const d = new Date(iso)
  const date = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
  if (daysUntilExpiry === null || daysUntilExpiry === undefined) return date
  if (daysUntilExpiry < 0) return `${date} · expired ${Math.abs(daysUntilExpiry)}d ago`
  if (daysUntilExpiry === 0) return `${date} · expires today`
  if (daysUntilExpiry <= 30) return `${date} · ${daysUntilExpiry}d left`
  return date
}

const CompliancePage = ({ buildingPassportId, embedded = false, onTabChange, chromeless = false }) => {
  const params = useParams()
  const bpId = buildingPassportId ?? params.bpId
  const navigate = useNavigate()
  const location = useLocation()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [deepDive, setDeepDive] = useState(null)
  const [history, setHistory] = useState([])
  const [expanded, setExpanded] = useState({})
  const [customiseTarget, setCustomiseTarget] = useState(null)
  const [previewFile, setPreviewFile] = useState(null)
  const [denyTarget, setDenyTarget] = useState(null)
  const [reviewBusy, setReviewBusy] = useState(null)
  const [approvingAll, setApprovingAll] = useState(false)
  const [unseenIds, setUnseenIds] = useState(new Set())
  const [unitNames, setUnitNames] = useState({})
  const [expandedGroups, setExpandedGroups] = useState(() => new Set())
  const [groupBusy, setGroupBusy] = useState(null)
  const [visibleUnits, setVisibleUnits] = useState({})

  const severityFromWeight = w => w >= 4 ? 'critical' : w === 3 ? 'high' : w === 2 ? 'medium' : 'low'

  const handleApproveEvidence = async (obl, fileId, unitId = null) => {
    const busyKey = unitId ? `${obl.id}:${unitId}` : obl.id
    if (reviewBusy) return
    setReviewBusy(busyKey)
    try {
      const payload = unitId
        ? { verdict: 'approved', unitId, fileId }
        : { verdict: 'approved', fileId }
      await reviewObligationEvidence(bpId, obl.id, payload)
      toast.success(unitId ? 'Unit evidence approved.' : 'Evidence approved.')
      notifyComplianceChanged()
      await reloadPreservingScroll()
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Could not approve evidence.'))
    } finally {
      setReviewBusy(null)
    }
  }

  const handleApproveAll = async () => {
    if (approvingAll) return
    setApprovingAll(true)
    try {
      const res = await bulkApproveEvidence(bpId)
      const n = res.data?.data?.approved || 0
      toast.success(n > 0
        ? `Approved ${n} matched ${n === 1 ? 'document' : 'documents'}.`
        : 'No matched documents were ready to approve.')
      notifyComplianceChanged()
      await reloadPreservingScroll()
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Could not approve evidence.'))
    } finally {
      setApprovingAll(false)
    }
  }

  const handleApproveAllUnits = async (obl, units) => {
    if (groupBusy) return
    setGroupBusy(obl.id)
    try {
      const res = await reviewObligationEvidence(bpId, obl.id, {
        verdict: 'approved',
        units: units.map(u => ({ unitId: u.unitId, fileId: u.evFileId })),
      })
      const n = res?.data?.data?.applied ?? units.length
      toast.success(n > 0
        ? `Approved ${n} unit ${n === 1 ? 'document' : 'documents'}.`
        : 'Nothing was approved. These documents no longer count as evidence for their units.')
      notifyComplianceChanged()
      await reloadPreservingScroll()
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Could not approve unit documents.'))
    } finally {
      setGroupBusy(null)
    }
  }

  const toggleGroup = oblId => setExpandedGroups(prev => {
    const next = new Set(prev)
    if (next.has(oblId)) next.delete(oblId)
    else next.add(oblId)
    return next
  })

  const handleDenyEvidence = async (reason, followUp) => {
    if (!denyTarget || reviewBusy) return
    const { obl, fileId, unitId } = denyTarget
    setReviewBusy(unitId ? `${obl.id}:${unitId}` : obl.id)
    try {
      const payload = unitId
        ? { verdict: 'denied', unitId, fileId, denialReason: reason || null }
        : { verdict: 'denied', fileId, denialReason: reason || null }
      await reviewObligationEvidence(bpId, obl.id, payload)
      toast.success(unitId
        ? 'Unit evidence denied. This unit is waiting for evidence again.'
        : 'Evidence denied. This obligation is waiting for evidence again.')
      notifyComplianceChanged()
      setDenyTarget(null)
      if (followUp === 'reupload') {
        const fileTypeId = obl.fileTypeId || obl.file_type_id
        const q = fileTypeId ? `?method=traditional&type=${encodeURIComponent(fileTypeId)}` : ''
        openInNewTab(`/files/upload/${bpId}${q}`)
        await reloadPreservingScroll()
        return
      }
      if (followUp === 'task') {
        const params = new URLSearchParams({
          title: `Upload evidence: ${obl.name}`,
          description: `Previous evidence was rejected${reason ? `: ${reason}` : ''}. Upload a valid ${obl.fileTypeName || obl.file_type_name || 'document'} for ${obl.regulatoryFramework || obl.regulatory_framework || 'this obligation'}.`,
          bpId,
          source: 'compliance',
          programmeObligationId: obl.id,
          returnTo: window.location.pathname,
        })
        const domainId = obl.taskDomainId || obl.task_domain_id
        if (domainId) params.set('taskDomainId', domainId)
        const priority = { low: 'low', medium: 'medium', high: 'high', critical: 'critical' }[obl.criticality]
        if (priority) params.set('priority', priority)
        const nextDue = obl.nextDueAt || obl.next_due_at
        if (nextDue) params.set('deadline', nextDue)
        if (unitId) {
          params.set('unitIds', unitId)
          params.set('unitMode', 'fan_out')
        }
        navigate(`/tasks/create?${params.toString()}`)
        return
      }
      await reloadPreservingScroll()
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Could not deny evidence.'))
    } finally {
      setReviewBusy(null)
    }
  }

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true)
    try {
      const [deepRes, histRes] = await Promise.allSettled([
        getComplianceDeepDive(bpId),
        getComplianceHistory(bpId, { days: 90 }),
      ])
      if (deepRes.status === 'rejected') throw deepRes.reason
      setDeepDive(deepRes.value.data?.data || null)
      setHistory(histRes.status === 'fulfilled' ? (histRes.value.data?.data || []) : [])
      setError(null)
    } catch (e) {
      setError(apiErrorMessage(e, 'Could not load compliance dashboard.'))
    } finally {
      setLoading(false)
    }
  }, [bpId])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    let cancelled = false
    fetchBpUnits({ bpId })
      .then(res => {
        if (cancelled) return
        const list = res?.data?.data || res?.data || []
        const map = {}
        ;(Array.isArray(list) ? list : []).forEach(u => { map[u.id] = u.name || u.displayName || u.address || 'Unit' })
        setUnitNames(map)
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [bpId])

  useEffect(() => {
    if (!deepDive) return
    const rows = (deepDive.byDomain || deepDive.by_domain || [])
      .flatMap(d => d.obligations || [])
    if (rows.length === 0) return
    const storageKey = `compliance-last-visit-${bpId}`
    const lastVisit = localStorage.getItem(storageKey)
    if (lastVisit) {
      const cutoff = new Date(lastVisit).getTime()
      setUnseenIds(new Set(rows
        .filter(o => {
          const created = o.createdAt || o.created_at
          return created && new Date(created).getTime() > cutoff
        })
        .map(o => o.id)))
    } else {
      setUnseenIds(new Set())
    }
    try { localStorage.setItem(storageKey, new Date().toISOString()) } catch (e) { /* quota */ }
  }, [deepDive, bpId])

  const canDecide = deepDive?.canDecideCompliance ?? false
  const tracksCompliance = deepDive?.tracksCompliance ?? true
  const liveScore = deepDive?.rollup?.score ?? null
  const liveRollup = deepDive?.rollup || {}
  const byDomain = deepDive?.byDomain || deepDive?.by_domain || []
  const inventory = deepDive?.evidenceInventory || deepDive?.evidence_inventory || []
  const availableTags = deepDive?.availableTags || deepDive?.available_tags || []

  const reviewQueue = useMemo(() => {
    const items = []
    const groupMap = new Map()
    byDomain.forEach(dom => (dom.obligations || []).forEach(obl => {
      if (obl.scope === 'unit') {
        const coverage = obl.unitCoverage || obl.unit_coverage || null
        if ((coverage?.applicable ?? 0) > 0) {
          ;(coverage.units || []).forEach(u => {
            if ((u.state || 'pending') !== 'in_review') return
            if (!groupMap.has(obl.id)) groupMap.set(obl.id, { obl, units: [] })
            groupMap.get(obl.id).units.push({
              unitId: u.unitId || u.unit_id,
              evFileId: u.fileId || u.file_id,
              expiryDate: u.expiryDate || u.expiry_date || null,
            })
          })
          return
        }
      }
      const ev = obl.evidence
      if (!ev) return
      if (obl.verdict !== 'in_review') return
      const evFileId = ev.fileId || ev.file_id
      const reviewStatus = obl.evidenceReviewStatus || obl.evidence_review_status
      const reviewedFileId = obl.evidenceReviewedFileId || obl.evidence_reviewed_file_id
      const settled = reviewedFileId && reviewedFileId === evFileId
        && (reviewStatus === 'approved' || reviewStatus === 'denied')
      if (!settled) {
        const needsDate = ((obl.cadenceDays ?? obl.cadence_days ?? 0) > 0)
          && !(ev.expiryDate || ev.expiry_date || ev.nextInspectionDate || ev.next_inspection_date)
        items.push({ obl, ev, evFileId, needsDate })
      }
    }))
    const unitGroups = Array.from(groupMap.values())
    const total = items.length + unitGroups.reduce((sum, g) => sum + g.units.length, 0)
    const approvableCount = items.filter(i => !i.needsDate).length
      + unitGroups.reduce((sum, g) => sum + g.units.length, 0)
    return { items, unitGroups, total, approvableCount }
  }, [byDomain])

  useEffect(() => {
    if (loading || location.hash !== '#review') return
    const el = document.getElementById('review')
    if (!el) return
    el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    navigate(`${location.pathname}${location.search}`, { replace: true })
  }, [loading, location.hash, reviewQueue.total, navigate, location.pathname, location.search])

  const reloadPreservingScroll = async () => {
    const y = window.scrollY
    await load({ silent: true })
    requestAnimationFrame(() => window.scrollTo({ top: y }))
  }

  useRefetchOnFocus(() => load({ silent: true }))

  const allExpanded = useMemo(
    () => byDomain.length > 0 && byDomain.every(d => expanded[d.domain?.id || 'unclassified']),
    [byDomain, expanded],
  )

  const toggleAll = () => {
    if (allExpanded) { setExpanded({}); return }
    const next = {}
    byDomain.forEach(d => { next[d.domain?.id || 'unclassified'] = true })
    setExpanded(next)
  }

  if (loading) {
    return <ComplianceShell activeTab='overview' bpId={bpId} embedded={embedded} onTabChange={onTabChange} chromeless={chromeless}><SkeletonRows rows={6} /></ComplianceShell>
  }
  if (error) {
    return <ComplianceShell activeTab='overview' bpId={bpId} embedded={embedded} onTabChange={onTabChange} chromeless={chromeless}><EmptyBox>{error}</EmptyBox></ComplianceShell>
  }

  return (
    <ComplianceShell activeTab='overview' bpId={bpId} embedded={embedded} onTabChange={onTabChange} chromeless={chromeless} score={tracksCompliance ? liveScore : null}>
      <Hero>
        <HeroLeft>
          <Eyebrow>Compliance score</Eyebrow>
          <Big data-tour-id='compliance.score-value'>{tracksCompliance ? formatPct(liveScore) : 'Not tracked'}</Big>
          <Sub>{tracksCompliance ? `${liveRollup.satisfied || 0} of ${liveRollup.total || 0} obligations satisfied` : 'Tracking is off. Turn it on to see your score.'}</Sub>
          <HeroExplainer>
            <ComplianceScoreExplainer align='left' />
          </HeroExplainer>
        </HeroLeft>
        <HeroStats>
          {COMPLIANCE_STATUS_BUCKETS.map(bucket => {
            const cell = {
              ...bucket,
              status: bucket.key === 'inReview' ? 'in_review' : bucket.key,
              value: liveRollup[bucket.key] ?? liveRollup[bucket.key === 'inReview' ? 'in_review' : bucket.key] ?? 0,
            }
            return (
            <StatCell
              key={cell.key}
              $tint={cell.tint}
              $clickable
              onClick={() => navigate(`/bp-list/${bpId}/programme?status=${cell.status}`)}
              title={`${cell.help} Click to open the compliance tracker filtered to ${cell.label.toLowerCase()}.`}
            >
              <StatNum>{cell.value}</StatNum>
              <StatLabel>{cell.label}</StatLabel>
            </StatCell>
            )
          })}
        </HeroStats>
        <HeroTrend>
          <SectionEyebrow>90-day trend</SectionEyebrow>
          <ComplianceTrendChart history={history} />
        </HeroTrend>
      </Hero>

      <SectionHead>
        <SectionTitle>
          Score by domain
          <ChartKeyExplainer
            title='Score by domain'
            lead='One row per compliance area. The bar under each one shows how that area&apos;s obligations split.'
            buckets={COMPLIANCE_STATUS_BUCKETS}
          />
        </SectionTitle>
        <SectionActions>
          <TextLink onClick={() => navigate(`/bp-list/${bpId}/programme`)}>
            Open compliance tracker <LaunchRoundedIcon style={{ fontSize: 13 }} />
          </TextLink>
        </SectionActions>
      </SectionHead>

      {byDomain.length === 0 ? (
        <EmptyBox>
          No programme obligations have been generated for this Building Passport yet.
          <TextLink onClick={() => navigate(`/bp-list/${bpId}/compliance/setup`)}>Open obligation list →</TextLink>
        </EmptyBox>
      ) : byDomain.map(dom => {
        const key = dom.domain?.id || 'unclassified'
        const rollup = dom.rollup || {}
        const score = rollup.score
        const unseenInDomain = (dom.obligations || []).filter(o => unseenIds.has(o.id)).length
        return (
          <DomainCard key={key}>
            <DomainHead onClick={() => navigate(`/bp-list/${bpId}/programme${dom.domain?.id ? `?domain=${dom.domain.id}` : ''}`)}>
              <DomainHeadLeft>
                <DomainDot style={{ background: dom.domain?.color || tintFor(score) }} />
                <DomainName>{dom.domain?.name || 'Unclassified'}</DomainName>
                <DomainCount>{rollup.satisfied || 0} / {rollup.total || 0}</DomainCount>
                {unseenInDomain > 0 && (
                  <UnseenBadge title={`${unseenInDomain} new since your last visit`}>
                    {unseenInDomain} new
                  </UnseenBadge>
                )}
              </DomainHeadLeft>
              <DomainHeadRight>
                <ScoreChip $tint={tintFor(score)}>{formatPct(score)}</ScoreChip>
                <LaunchRoundedIcon style={{ fontSize: 15, color: '#9aa0aa' }} />
              </DomainHeadRight>
            </DomainHead>
            {complianceBucketTotal(rollup) > 0 && (
              <DomainMix
                title={complianceBucketsFromRollup(rollup)
                  .filter(b => b.count > 0)
                  .map(b => `${b.label}: ${b.count}`)
                  .join(' · ')}
              >
                {complianceBucketsFromRollup(rollup)
                  .filter(b => b.count > 0)
                  .map(b => (
                    <DomainMixSeg
                      key={b.key}
                      style={{
                        width: `${(b.count / complianceBucketTotal(rollup)) * 100}%`,
                        background: b.tint,
                      }}
                    />
                  ))}
              </DomainMix>
            )}
          </DomainCard>
        )
      })}

      {byDomain.length > 0 && (
        <MixKey>
          {COMPLIANCE_STATUS_BUCKETS.map(b => (
            <MixKeyItem key={b.key} title={b.help}>
              <MixKeyDot style={{ background: b.tint }} />{b.label}
            </MixKeyItem>
          ))}
        </MixKey>
      )}

      {reviewQueue.total > 0 && canDecide && (
        <OnceCallout
          storageKey='bp_review_gating_explained'
          tone='review'
          title='Uploaded isn’t counted yet'
          icon={<VerifiedRoundedIcon style={{ fontSize: 20 }} />}
        >
          A document only counts towards your compliance score once you’ve approved it here. We’ve matched
          the files below to their obligations. Open each one, check it is the right evidence, then approve
          (or deny) it. Approved evidence updates your score straight away.
        </OnceCallout>
      )}

      {reviewQueue.total > 0 && (
        <>
          <SectionHead id='review'>
            <ReviewHeadText>
              <SectionTitle>{canDecide ? 'Documents to approve' : 'Documents awaiting approval'}</SectionTitle>
              <SectionSub>
                We automatically matched {reviewQueue.total} uploaded document{reviewQueue.total === 1 ? '' : 's'} to {reviewQueue.total === 1 ? 'an obligation' : 'obligations'}.
                {canDecide
                  ? ' Approve each one to count it towards your compliance score, or deny to remove the match. Nothing counts until you approve it.'
                  : ' The owner or responsible person approves these. Nothing counts towards the score until then.'}
                {reviewQueue.unitGroups.length > 0 && ' Per-unit documents are grouped by obligation below. Approve a whole set in one go, or expand to review unit by unit.'}
              </SectionSub>
            </ReviewHeadText>
            {canDecide && reviewQueue.approvableCount > 0 && (
              <ApproveAllBtn disabled={approvingAll} onClick={handleApproveAll}>
                {approvingAll ? 'Approving…' : `Approve all matched${reviewQueue.approvableCount < reviewQueue.total ? ` (${reviewQueue.approvableCount})` : ''}`}
              </ApproveAllBtn>
            )}
          </SectionHead>
          <ReviewList>
            {reviewQueue.items.map(({ obl, ev, evFileId, needsDate }) => (
              <ReviewRow key={obl.id}>
                <EvidenceFile onClick={() => setPreviewFile({ id: evFileId, displayName: ev.displayName || ev.display_name })}>
                  {ev.thumbnailUrl
                    ? <Thumb src={ev.thumbnailUrl} alt='' />
                    : <ThumbPlaceholder>FILE</ThumbPlaceholder>}
                  <EvidenceMeta>
                    <EvidenceName>{ev.displayName || ev.display_name}</EvidenceName>
                    <EvidenceSub>for {obl.name}</EvidenceSub>
                    {(() => {
                      const matchedType = ev.fileTypeName || ev.file_type_name
                      if (!matchedType) return null
                      const primaryType = obl.fileTypeName || obl.file_type_name
                      const isPrimaryMatch = primaryType && matchedType === primaryType
                      const acceptedNames = (obl.acceptedFileTypes || obl.accepted_file_types || []).map(t => t.name).filter(Boolean)
                      const matchReason = `“${obl.name}” is evidenced by documents of type: ${acceptedNames.join(', ') || primaryType || 'not set'}. This file is classified as “${matchedType}”, so it was matched to the obligation automatically. Approve to confirm it counts towards your compliance score; deny to remove the match.`
                      return <MatchExplainer matchedType={matchedType} isPrimaryMatch={isPrimaryMatch} reason={matchReason} />
                    })()}
                    {needsDate && (
                      <NeedsDateNote>
                        No date on this document. Add an expiry or inspection date for it to count.
                        <NeedsDateCta onClick={e => { e.stopPropagation(); setPreviewFile({ id: evFileId, displayName: ev.displayName || ev.display_name }) }}>Open &amp; add a date ↗</NeedsDateCta>
                      </NeedsDateNote>
                    )}
                  </EvidenceMeta>
                </EvidenceFile>
                <ReviewRowRight>
                  <ObligationScopePill
                    bpId={bpId}
                    obligation={obl}
                    canEdit={canDecide}
                    onChanged={reloadPreservingScroll}
                  />
                  {canDecide ? (
                    <ReviewButtons>
                      <ReviewBtn $tone='ok' disabled={reviewBusy === obl.id || needsDate}
                        title={needsDate ? 'Add a date to the document first. Approving will not count it until then.' : undefined}
                        onClick={() => handleApproveEvidence(obl, evFileId)}>
                        <CheckRoundedIcon style={{ fontSize: 13 }} /> Approve
                      </ReviewBtn>
                      <ReviewBtn $tone='bad' disabled={reviewBusy === obl.id} onClick={() => setDenyTarget({ obl, fileId: evFileId })}>
                        <CloseRoundedIcon style={{ fontSize: 13 }} /> Deny
                      </ReviewBtn>
                    </ReviewButtons>
                  ) : (
                    <OwnerApprovalNote>Owner / responsible person approval needed</OwnerApprovalNote>
                  )}
                </ReviewRowRight>
              </ReviewRow>
            ))}

            {reviewQueue.unitGroups.map(({ obl, units }) => {
              const isOpen = expandedGroups.has(obl.id)
              const shown = visibleUnits[obl.id] || UNIT_PAGE
              const busy = groupBusy === obl.id
              return (
                <UnitGroupCard key={`grp-${obl.id}`} $open={isOpen}>
                  <UnitGroupHead>
                    <UnitGroupToggle onClick={() => toggleGroup(obl.id)} aria-expanded={isOpen}>
                      <Chevron $open={isOpen}>▾</Chevron>
                      <UnitGroupHeadMain>
                        <UnitGroupTitle>{obl.name}</UnitGroupTitle>
                        <UnitGroupSub>{units.length} unit{units.length === 1 ? '' : 's'} awaiting review · per-unit evidence</UnitGroupSub>
                      </UnitGroupHeadMain>
                    </UnitGroupToggle>
                    <UnitGroupActions>
                      <ObligationScopePill
                        bpId={bpId}
                        obligation={obl}
                        canEdit={canDecide}
                        onChanged={reloadPreservingScroll}
                      />
                      {canDecide ? (
                        <ReviewBtn $tone='ok' disabled={busy}
                          title={`Approve the matched document for all ${units.length} unit${units.length === 1 ? '' : 's'} in one go.`}
                          onClick={() => handleApproveAllUnits(obl, units)}>
                          <CheckRoundedIcon style={{ fontSize: 13 }} /> {busy ? 'Approving…' : `Approve all ${units.length}`}
                        </ReviewBtn>
                      ) : (
                        <OwnerApprovalNote>Owner / responsible person approval needed</OwnerApprovalNote>
                      )}
                    </UnitGroupActions>
                  </UnitGroupHead>
                  {isOpen && (
                    <UnitGroupBody>
                      {units.slice(0, shown).map(u => {
                        const rowKey = `${obl.id}:${u.unitId}`
                        const rowBusy = reviewBusy === rowKey
                        return (
                          <ReviewRow key={rowKey}>
                            <EvidenceFile
                              as={u.evFileId ? 'button' : 'div'}
                              onClick={u.evFileId ? () => setPreviewFile({ id: u.evFileId }) : undefined}
                            >
                              <ThumbPlaceholder>UNIT</ThumbPlaceholder>
                              <EvidenceMeta>
                                <EvidenceName>{unitNames[u.unitId] || 'Unit'}</EvidenceName>
                                {u.expiryDate && <EvidenceSub>Valid until {formatExpiry(u.expiryDate, null)}</EvidenceSub>}
                              </EvidenceMeta>
                            </EvidenceFile>
                            {canDecide && (
                              <ReviewButtons>
                                <ReviewBtn $tone='ok' disabled={rowBusy}
                                  title='Approve this unit’s document. Only this unit is affected.'
                                  onClick={() => handleApproveEvidence(obl, u.evFileId, u.unitId)}>
                                  <CheckRoundedIcon style={{ fontSize: 13 }} /> Approve
                                </ReviewBtn>
                                <ReviewBtn $tone='bad' disabled={rowBusy} onClick={() => setDenyTarget({ obl, fileId: u.evFileId, unitId: u.unitId })}>
                                  <CloseRoundedIcon style={{ fontSize: 13 }} /> Deny
                                </ReviewBtn>
                              </ReviewButtons>
                            )}
                          </ReviewRow>
                        )
                      })}
                      <InfiniteScrollSentinel
                        hasMore={shown < units.length}
                        loading={false}
                        onLoadMore={() => setVisibleUnits(v => ({ ...v, [obl.id]: (v[obl.id] || UNIT_PAGE) + UNIT_PAGE }))}
                      />
                    </UnitGroupBody>
                  )}
                </UnitGroupCard>
              )
            })}
          </ReviewList>
        </>
      )}

      {inventory.length > 0 && (
        <>
          <SectionHead>
            <SectionTitle>Evidence inventory</SectionTitle>
            <SectionSub>{inventory.length} file{inventory.length === 1 ? '' : 's'} linked as evidence for obligations</SectionSub>
          </SectionHead>
          <InventoryGrid>
            {inventory.map(file => (
              <InventoryTile key={file.fileId || file.file_id} onClick={() => setPreviewFile({ id: file.fileId || file.file_id, displayName: file.displayName || file.display_name })}>
                {file.thumbnailUrl
                  ? <InventoryThumb src={file.thumbnailUrl} alt='' />
                  : <InventoryThumbPlaceholder>FILE</InventoryThumbPlaceholder>}
                <InventoryName>{file.displayName || file.display_name}</InventoryName>
                <InventoryType>{file.fileTypeName || file.file_type_name}</InventoryType>
                {(file.verdict === 'expired' || file.verdict === 'in_review') && (
                  <VerdictChip $tone={verdictTint(file.verdict)} style={{ alignSelf: 'flex-start' }}>
                    {file.verdict === 'expired' ? 'Expired' : 'In review'}
                  </VerdictChip>
                )}
                {(() => {
                  const obs = file.obligationsSatisfied || file.obligations_satisfied || []
                  if (obs.length > 0) {
                    return (
                      <>
                        <InventoryCount>Covers {obs.length} obligation{obs.length === 1 ? '' : 's'}</InventoryCount>
                        <CoverageList>
                          {obs.slice(0, 4).map(o => <CoverageItem key={o.id} title={o.name}>{o.name}</CoverageItem>)}
                          {obs.length > 4 && <CoverageMore>+{obs.length - 4} more</CoverageMore>}
                        </CoverageList>
                      </>
                    )
                  }
                  const matched = file.obligationsMatched || file.obligations_matched || []
                  if (matched.length === 0) return null
                  return (
                    <>
                      <InventoryCount>Matched to {matched.length} obligation{matched.length === 1 ? '' : 's'} · not yet counted</InventoryCount>
                      <CoverageList>
                        {matched.slice(0, 4).map(o => <CoverageItem key={o.id} title={o.name}>{o.name}</CoverageItem>)}
                        {matched.length > 4 && <CoverageMore>+{matched.length - 4} more</CoverageMore>}
                      </CoverageList>
                    </>
                  )
                })()}
                {(file.expiryDate || file.expiry_date) && (
                  <InventoryExpiry>{formatExpiry(file.expiryDate || file.expiry_date, null)}</InventoryExpiry>
                )}
              </InventoryTile>
            ))}
          </InventoryGrid>
        </>
      )}

      {customiseTarget && (
        <CustomiseObligationModal
          bpId={bpId}
          bpName={deepDive?.buildingName || null}
          obligation={customiseTarget}
          availableTags={availableTags}
          onClose={() => setCustomiseTarget(null)}
          onSaved={() => load({ silent: true })}
        />
      )}

      {previewFile && (
        <GuruInsightModal
          open
          onClose={() => setPreviewFile(null)}
          file={previewFile}
          bpId={bpId}
          onUpdate={() => load({ silent: true })}
        />
      )}

      {denyTarget && (
        <DenyEvidenceModal
          obligation={denyTarget.obl}
          unitName={denyTarget.unitId ? (unitNames[denyTarget.unitId] || 'this unit') : null}
          busy={reviewBusy === (denyTarget.unitId ? `${denyTarget.obl.id}:${denyTarget.unitId}` : denyTarget.obl.id)}
          onClose={() => setDenyTarget(null)}
          onDeny={handleDenyEvidence}
        />
      )}
    </ComplianceShell>
  )
}

const MatchExplainer = ({ matchedType, isPrimaryMatch, reason }) => {
  const [show, setShow] = useState(false)
  return (
    <MatchNote>
      <MatchRow>
        <span>Matched on document type “{matchedType}”{isPrimaryMatch ? '' : ' · AI-linked'}</span>
        <MatchHelp type='button' onClick={e => { e.stopPropagation(); setShow(s => !s) }}>
          {show ? 'Hide' : 'Why?'}
        </MatchHelp>
      </MatchRow>
      {show && <MatchReason>{reason}</MatchReason>}
    </MatchNote>
  )
}

const DenyEvidenceModal = ({ obligation, unitName, busy, onClose, onDeny }) => {
  const [reason, setReason] = useState('')
  return createPortal(
    <DenyBackdrop onClick={onClose}>
      <DenyCard onClick={e => e.stopPropagation()}>
        <DenyTitle>Deny evidence</DenyTitle>
        <DenySub>
          {unitName
            ? <>The current document for <strong>{unitName}</strong> will stop counting towards <strong>{obligation.name}</strong> and this unit returns to awaiting evidence. Only this unit is affected.</>
            : <>The current file will stop satisfying <strong>{obligation.name}</strong> and the obligation returns to awaiting evidence.</>}
        </DenySub>
        <DenyLabel>Why is it being rejected?</DenyLabel>
        <DenyTextarea
          rows={3}
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder='e.g. Report covers the wrong inspection period…' />
        <DenyActions>
          <DenyGhostBtn onClick={onClose} disabled={busy}>Cancel</DenyGhostBtn>
          <DenyGhostBtn onClick={() => onDeny(reason, null)} disabled={busy}>
            Deny only
          </DenyGhostBtn>
          <DenyGhostBtn onClick={() => onDeny(reason, 'task')} disabled={busy}>
            Deny &amp; create task
          </DenyGhostBtn>
          <DenyPrimaryBtn onClick={() => onDeny(reason, 'reupload')} disabled={busy}>
            Deny &amp; re-upload document
          </DenyPrimaryBtn>
        </DenyActions>
      </DenyCard>
    </DenyBackdrop>,
    document.body
  )
}

export default CompliancePage

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`

/*
 * Three blocks of very different heights sat in fixed grid columns and were
 * centred individually, so nothing lined up and whichever column had the least
 * content left a gap. Flex with wrap keeps them on one line while they fit,
 * lets the stats absorb the leftover width, and stacks them cleanly when they
 * do not — aligned to the top either way.
 */
const Hero = styled.section`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 20px 24px;
  padding: 22px 26px;
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 18px;
  margin-bottom: 18px;
  animation: ${fadeUp} 320ms ease both;

  > * { min-width: 0; }

  @media (max-width: 768px) {
    padding: 16px 14px;
    gap: 16px;
    border-radius: 14px;
  }
`

const HeroLeft = styled.div`
  display: flex; flex-direction: column; gap: 4px;
  flex: 0 1 auto;
  min-width: 180px;

  @media (max-width: 768px) { flex: 1 1 100%; }
`

const Eyebrow = styled.span`
  font-size: 12px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-weight: 800;
`

const Big = styled.span`
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: 56px;
  letter-spacing: -1.6px;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.colors.haiti};

  @media (max-width: 768px) { font-size: 44px; letter-spacing: -1.1px; }
`

const Sub = styled.span`
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const HeroExplainer = styled.div`
  margin-top: 8px;
`

const HeroStats = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 10px;
  flex: 1 1 360px;

  @media (max-width: 768px) {
    flex: 1 1 100%;
    grid-template-columns: repeat(auto-fit, minmax(88px, 1fr));
    gap: 8px;
  }
`

const StatCell = styled.div`
  font: inherit;
  text-align: left;
  padding: 12px 10px;
  min-width: 0;

  @media (max-width: 768px) { padding: 10px 8px; }
  border-radius: 12px;
  background: ${({ $tint }) => `${$tint}1A`};
  border: 1px solid ${({ $tint, $active }) => $active ? $tint : `${$tint}33`};
  display: flex; flex-direction: column; gap: 2px;
  cursor: pointer;
  transition: all 160ms ease;
  box-shadow: ${({ $tint, $active }) => $active ? `0 0 0 3px ${$tint}26` : 'none'};
  &:hover { border-color: ${({ $tint }) => $tint}; transform: translateY(-1px); }
`

const UnseenDot = styled.span`
  display: inline-block;
  width: 8px; height: 8px;
  border-radius: 50%;
  background: #b00020;
  margin-right: 7px;
  flex-shrink: 0;
  box-shadow: 0 0 0 3px rgba(204, 34, 0, 0.16);
  vertical-align: middle;
`

const UnseenBadge = styled.span`
  display: inline-flex; align-items: center; gap: 5px;
  padding: 2px 9px;
  background: rgba(204, 34, 0, 0.08);
  color: #b00020;
  border: 1px solid rgba(204, 34, 0, 0.28);
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.04em;
  &::before {
    content: '';
    width: 6px; height: 6px;
    border-radius: 50%;
    background: #b00020;
  }
`

const FilterNotice = styled.div`
  display: flex; align-items: center; gap: 10px;
  padding: 9px 14px;
  margin-bottom: 12px;
  background: rgba(51, 190, 204, 0.07);
  border: 1px solid rgba(51, 190, 204, 0.24);
  border-radius: 10px;
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.haiti};
  strong { text-transform: capitalize; }
`

const FilterClear = styled.button`
  font: inherit;
  margin-left: auto;
  background: none;
  border: none;
  padding: 0;
  color: ${({ theme }) => theme.colors.secondary};
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
  &:hover { text-decoration: underline; }
`

const StatNum = styled.span`
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: 26px;
  letter-spacing: -0.6px;
  line-height: 1;
  color: ${({ theme }) => theme.colors.haiti};
`

const StatLabel = styled.span`
  font-size: 12px;
  color: #475569;
  font-weight: 700;
`

const HeroTrend = styled.div`
  display: flex; flex-direction: column; gap: 6px;
  flex: 1 1 260px;
  max-width: 100%;

  @media (max-width: 768px) { flex: 1 1 100%; }
`

const SectionEyebrow = styled.span`
  font-size: 12px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-weight: 800;
`

const SectionHead = styled.div`
  display: flex; justify-content: space-between; align-items: baseline;
  margin: 24px 0 12px;
  gap: 16px;
`

const ReviewHeadText = styled.div`
  display: flex; flex-direction: column; gap: 2px;
`

const ApproveAllBtn = styled.button`
  font: inherit; cursor: pointer;
  border: none; border-radius: 999px;
  padding: 8px 16px;
  font-size: 12.5px; font-weight: 800; letter-spacing: 0.02em;
  color: #fff; background: #d97706;
  align-self: center;
  transition: background 160ms ease;
  &:hover { background: #855207; }
  &:disabled { opacity: 0.6; cursor: default; }
`

const SectionTitle = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
  display: inline-flex;
  align-items: center;
  gap: 7px;
`

const SectionActions = styled.div`
  display: flex; gap: 16px; align-items: center;
`

const SectionSub = styled.span`
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const TextLink = styled.button`
  font: inherit;
  background: none; border: none; padding: 0;
  color: ${({ theme }) => theme.colors.haiti};
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex; align-items: center; gap: 4px;
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 4px;
  opacity: 0.78;
  &:hover { opacity: 1; }
`

const DomainMix = styled.div`
  display: flex; height: 8px; border-radius: 4px; overflow: hidden;
  background: #f1f5f9; margin: 0 14px 12px; cursor: help;
`
const DomainMixSeg = styled.span`height: 100%; min-width: 2px;`
const MixKey = styled.div`
  display: flex; flex-wrap: wrap; gap: 6px 16px; margin: 2px 2px 18px;
`
const MixKeyItem = styled.span`
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 13px; color: #475569; white-space: nowrap;
`
const MixKeyDot = styled.span`width: 10px; height: 10px; border-radius: 3px; flex-shrink: 0;`
const DomainCard = styled.section`
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 14px;
  margin-bottom: 10px;
  overflow: hidden;
`

const DomainHead = styled.header`
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 18px;
  cursor: pointer;
  user-select: none;
  &:hover { background: #fafafa; }
`

const DomainHeadLeft = styled.div`
  display: flex; align-items: center; gap: 12px;
`

const DomainHeadRight = styled.div`
  display: flex; align-items: center; gap: 12px;
`

const DomainDot = styled.span`
  width: 10px; height: 10px; border-radius: 50%;
`

const DomainName = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
`

const DomainCount = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const ScoreChip = styled.span`
  font-size: 12px;
  font-weight: 800;
  padding: 4px 10px;
  border-radius: 999px;
  background: ${({ $tint }) => `${$tint}1A`};
  color: ${({ $tint }) => $tint};
  border: 1px solid ${({ $tint }) => `${$tint}33`};
  font-variant-numeric: tabular-nums;
`

const ObligationList = styled.div`
  border-top: 1px solid #f4f4f6;
`

const VerdictChip = styled.span`
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 4px 9px;
  border-radius: 999px;
  text-align: center;
  background: ${({ $tone }) => `${$tone}1A`};
  color: ${({ $tone }) => $tone};
  border: 1px solid ${({ $tone }) => `${$tone}33`};
`

const ObligationBody = styled.div`
  display: flex; flex-direction: column; gap: 4px; min-width: 0;
`

const ObligationName = styled.span`
  display: inline-flex; align-items: center;
  font-size: 13.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
`

const ObligationMeta = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.grey[800]};
  display: flex; gap: 10px; flex-wrap: wrap;
`

const ScopeNote = styled.span`
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
`

const EvidenceCell = styled.div`
  display: flex; flex-direction: column; gap: 4px; align-items: stretch;
`

const ReviewList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 24px;
`

const UnitGroupCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ $open }) => $open ? '#d7d7dd' : '#ececec'};
  border-radius: 12px;
  overflow: hidden;
`

const UnitGroupHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  flex-wrap: wrap;
`

const UnitGroupToggle = styled.button`
  font: inherit;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  text-align: left;
  min-width: 0;
  flex: 1;
`

const Chevron = styled.span`
  font-size: 13px;
  line-height: 1;
  color: ${({ theme }) => theme.colors.grey[800]};
  transform: rotate(${({ $open }) => $open ? '0deg' : '-90deg'});
  transition: transform 0.15s ease;
  flex-shrink: 0;
`

const UnitGroupHeadMain = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`

const UnitGroupTitle = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
`

const UnitGroupSub = styled.span`
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.grey[800]};
  margin-top: 1px;
`

const UnitGroupActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`

const UnitGroupBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 14px 12px 34px;
  border-top: 1px solid #f0f0f2;
`

const ReviewRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 12px 14px;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid #ececec;
  border-radius: 12px;
  flex-wrap: wrap;
`

const ReviewRowRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`

const EvidenceFile = styled.button`
  font: inherit;
  background: ${({ theme }) => theme.colors.lighterGrey};
  border: 1px solid #ececec;
  border-radius: 10px;
  padding: 6px 10px;
  cursor: pointer;
  display: grid;
  grid-template-columns: 36px 1fr;
  gap: 10px;
  align-items: center;
  text-align: left;
  &:hover { background: #f1f1f3; }
`

const Thumb = styled.img`
  width: 36px; height: 36px;
  object-fit: cover;
  border-radius: 6px;
`

const ThumbPlaceholder = styled.span`
  width: 36px; height: 36px;
  border-radius: 6px;
  background: #e4e4e7;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 9px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const EvidenceMeta = styled.div`
  display: flex; flex-direction: column; min-width: 0;
`

const EvidenceName = styled.span`
  font-size: 13.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const EvidenceSub = styled.span`
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.grey[800]};
  margin-top: 1px;
`

const MatchNote = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 4px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const MatchRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
`

const MatchHelp = styled.button`
  font: inherit;
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-decoration: underline;
  &:hover { opacity: 0.8; }
`

const MatchReason = styled.p`
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.grey[800]};
  background: #f8f9fb;
  border: 1px solid #eceef2;
  border-radius: 8px;
  padding: 8px 10px;
`

const OwnerApprovalNote = styled.span`
  font-size: 12px;
  font-style: italic;
  color: ${({ theme }) => theme.colors.grey[800]};
  text-align: right;
  max-width: 160px;
`

const NoEvidence = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  padding: 8px 10px;
  background: #b000200a;
  border: 1px dashed #b0002033;
  border-radius: 10px;
`

const NoEvidenceText = styled.span`
  font-size: 11.5px;
  color: #b00020;
  font-weight: 600;
`

const NoEvidenceCta = styled.button`
  font: inherit;
  background: none; border: none; padding: 0;
  color: #b00020;
  font-size: 11.5px;
  font-weight: 800;
  cursor: pointer;
  &:hover { text-decoration: underline; }
`

const ReviewButtons = styled.div`
  display: flex; gap: 6px;
  margin-top: 6px;
`

const ReviewBtn = styled.button`
  font: inherit;
  flex: 1;
  display: inline-flex; align-items: center; justify-content: center; gap: 4px;
  padding: 5px 10px;
  background: ${({ $tone }) => $tone === 'ok' ? 'rgba(42,166,179,0.10)' : 'rgba(204,34,0,0.07)'};
  color: ${({ $tone }) => $tone === 'ok' ? '#2aa6b3' : '#b00020'};
  border: 1px solid ${({ $tone }) => $tone === 'ok' ? 'rgba(42,166,179,0.32)' : 'rgba(204,34,0,0.28)'};
  border-radius: 8px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.04em;
  cursor: pointer;
  transition: all 160ms ease;
  &:hover:not(:disabled) { filter: brightness(0.96); }
  &:disabled { opacity: 0.55; cursor: wait; }
`

const ReviewStamp = styled.span`
  display: inline-flex; align-items: center; gap: 5px;
  margin-top: 6px;
  align-self: flex-start;
  padding: 4px 11px;
  background: ${({ $tone }) => $tone === 'ok' ? 'rgba(42,166,179,0.12)' : 'rgba(204,34,0,0.08)'};
  color: ${({ $tone }) => $tone === 'ok' ? '#2aa6b3' : '#b00020'};
  border: 1px solid ${({ $tone }) => $tone === 'ok' ? 'rgba(42,166,179,0.36)' : 'rgba(204,34,0,0.30)'};
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`

const DenyBackdrop = styled.div`
  position: fixed; inset: 0;
  background: rgba(12, 18, 41, 0.45);
  display: flex; align-items: center; justify-content: center;
  z-index: 100020;
`

const DenyCard = styled.div`
  background: #fff;
  border-radius: 18px;
  padding: 24px 26px;
  width: min(460px, calc(100vw - 32px));
  display: flex; flex-direction: column; gap: 10px;
  box-shadow: 0 18px 48px rgba(12, 18, 41, 0.28);
`

const DenyTitle = styled.h3`
  margin: 0;
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: 22px;
  letter-spacing: -0.4px;
  color: ${({ theme }) => theme.colors.haiti};
`

const DenySub = styled.p`
  margin: 0;
  font-size: 12.5px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const DenyLabel = styled.label`
  font-size: 12px;
  letter-spacing: 0.10em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-weight: 800;
  margin-top: 4px;
`

const DenyTextarea = styled.textarea`
  font: inherit;
  padding: 9px 12px;
  border: 1px solid #ececec;
  border-radius: 8px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.haiti};
  resize: vertical;
  outline: none;
  &:focus { border-color: #33becc; box-shadow: 0 0 0 3px rgba(51,190,204,0.12); }
`

const DenyActions = styled.div`
  display: flex; gap: 8px; justify-content: flex-end;
  margin-top: 6px;
  flex-wrap: wrap;
`

const DenyGhostBtn = styled.button`
  font: inherit;
  padding: 9px 14px;
  background: transparent;
  border: 1px solid #ececec;
  color: ${({ theme }) => theme.colors.haiti};
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  &:hover:not(:disabled) { background: #f4f4f6; }
  &:disabled { opacity: 0.55; cursor: wait; }
`

const DenyPrimaryBtn = styled.button`
  font: inherit;
  padding: 9px 16px;
  background: linear-gradient(135deg, #33becc 0%, #2aa6b3 100%);
  border: none;
  color: #fff;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
  &:hover:not(:disabled) { filter: brightness(1.06); }
  &:disabled { opacity: 0.55; cursor: wait; }
`

const Satisfies = styled.div`
  display: flex; flex-wrap: wrap; gap: 6px;
  align-items: center;
  padding: 8px 12px;
  background: ${({ theme }) => theme.colors.lighterGrey};
  border-radius: 8px;
  margin-top: 4px;
`

const SatisfiesLabel = styled.span`
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const SatisfiesText = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.haiti};
`

const AcceptedRow = styled.div`
  display: inline-flex; flex-wrap: wrap; gap: 6px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.haiti};
  align-items: center;
`

const TypeChip = styled.span`
  font-size: 11px;
  font-weight: 700;
  padding: 3px 9px;
  border-radius: 999px;
  background: ${({ $primary }) => $primary ? '#33becc1A' : '#fff'};
  color: ${({ $primary }) => $primary ? '#1f8c97' : '#0c1229'};
  border: 1px solid ${({ $primary }) => $primary ? '#33becc55' : '#ececec'};
`

const TagBadge = styled.span`
  font-size: 11px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 6px;
  background: #d977061A;
  color: #B8662A;
  border: 1px solid #d9770655;
`

const SkipNote = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[800]};
  background: #9aa0aa1A;
  padding: 6px 10px;
  border-radius: 8px;
  margin-top: 4px;
  font-style: italic;
`

const NeedsDateNote = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #d97706;
  background: #fef3c7;
  border: 1px solid #d9770633;
  padding: 6px 10px;
  border-radius: 8px;
  margin-top: 6px;
`

const NeedsDateCta = styled.button`
  font: inherit;
  border: none;
  background: none;
  padding: 0;
  font-weight: 700;
  color: #d97706;
  cursor: pointer;
  &:hover { text-decoration: underline; }
`

const RowActions = styled.div`
  display: flex; gap: 14px;
  margin-top: 4px;
  align-items: center;
`

const RowAction = styled.button`
  font: inherit;
  background: none; border: none; padding: 0;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-size: 11.5px;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex; align-items: center; gap: 4px;
  &:hover:not(:disabled) { color: ${({ theme }) => theme.colors.haiti}; }
  &:disabled { opacity: 0.55; cursor: wait; }
`

const RowActionLink = styled.a`
  font: inherit;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-size: 11.5px;
  font-weight: 700;
  text-decoration: none;
  display: inline-flex; align-items: center; gap: 4px;
  &:hover { color: ${({ theme }) => theme.colors.haiti}; text-decoration: underline; }
`

const CitationLink = styled.a`
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.grey[800]};
  text-decoration: none;
  display: inline-flex; align-items: center; gap: 3px;
  align-self: flex-end;
  &:hover { color: ${({ theme }) => theme.colors.haiti}; }
`

const InventoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 12px;
`

const InventoryTile = styled.button`
  font: inherit;
  text-align: left;
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 12px;
  padding: 14px;
  display: flex; flex-direction: column; gap: 6px;
  cursor: pointer;
  &:hover { box-shadow: 0 4px 12px rgba(0,0,0,0.06); transform: translateY(-1px); }
  transition: transform 160ms ease, box-shadow 160ms ease;
`

const InventoryThumb = styled.img`
  width: 100%; height: 80px;
  object-fit: cover;
  border-radius: 8px;
`

const InventoryThumbPlaceholder = styled.div`
  width: 100%; height: 80px;
  border-radius: 8px;
  background: #f6f6f8;
  display: flex; align-items: center; justify-content: center;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.1em;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const InventoryName = styled.span`
  font-size: 13.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const InventoryType = styled.span`
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const InventoryCount = styled.span`
  font-size: 12.5px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
`

const CoverageList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 1px;
`

const CoverageItem = styled.span`
  font-size: 11.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.secondary};
  background: rgba(51, 190, 204, 0.10);
  border: 1px solid rgba(51, 190, 204, 0.24);
  border-radius: 999px;
  padding: 2px 8px;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const CoverageMore = styled.span`
  font-size: 11.5px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.grey[800]};
  align-self: center;
`

const InventoryExpiry = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const EmptyBox = styled.div`
  background: #fff;
  border: 1px dashed #ececec;
  border-radius: 14px;
  padding: 40px 24px;
  text-align: center;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-size: 13px;
`

const SecondaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  color: ${({ theme }) => theme.colors.haiti};
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 8px;
  padding: 8px 14px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  &:hover { background: ${({ theme }) => theme.colors.backgroundColor}; }
`
